import { ChangeDetectionStrategy, Component, computed, ElementRef, signal, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { IHeaderAngularComp } from 'ag-grid-angular';
import { IHeaderParams } from 'ag-grid-community';
import { DataInputColumnType } from '../../model';

export interface EditableHeaderParams {
  readonly editable: boolean;
  readonly onHeaderRenamed: (colId: string, newName: string) => void;
  readonly onColumnDeleted: (colId: string) => void;
  readonly onColumnTypeChanged: (colId: string, newType: DataInputColumnType) => void;
  readonly getIsActive: () => boolean;
  readonly getColType: () => DataInputColumnType;
  readonly onColumnSelected: (colId: string) => void;
}

type EditableHeaderComponentParams = IHeaderParams & EditableHeaderParams;

@Component({
  selector: 'dojo-editable-header',
  templateUrl: './editable-header.component.html',
  styleUrl: './editable-header.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule],
  host: {
    '[class.active]': 'isActive()',
    '(click)': 'onHostClick()',
    '(dblclick)': 'onHostDblClick()',
    '(contextmenu)': 'onContextMenu($event)',
  },
})
export class EditableHeaderComponent implements IHeaderAngularComp {
  readonly editing = signal(false);
  readonly headerName = signal('');
  readonly isActive = signal(false);
  readonly colType = signal<DataInputColumnType>('number');
  readonly contextMenuVisible = signal(false);
  readonly contextMenuPos = signal({ x: 0, y: 0 });
  readonly typePickerVisible = signal(false);
  readonly typePickerPos = signal({ x: 0, y: 0 });

  readonly typeIcon = computed(() => {
    switch (this.colType()) {
      case 'number':
        return '#';
      case 'text':
        return 'Aa';
      case 'boolean':
        return 'T/F';
    }
  });

  readonly typeLabel = computed(() => {
    switch (this.colType()) {
      case 'number':
        return 'Number';
      case 'text':
        return 'Text';
      case 'boolean':
        return 'True / False';
    }
  });

  private readonly headerInput = viewChild<ElementRef<HTMLInputElement>>('headerInput');

  private params!: EditableHeaderComponentParams;
  private clickTimer: ReturnType<typeof setTimeout> | null = null;

  agInit(params: EditableHeaderComponentParams): void {
    this.params = params;
    this.headerName.set(params.displayName);
    this.isActive.set(params.getIsActive());
    this.colType.set(params.getColType());
  }

  refresh(params: EditableHeaderComponentParams): boolean {
    this.params = params;
    this.headerName.set(params.displayName);
    this.isActive.set(params.getIsActive());
    this.colType.set(params.getColType());
    return true;
  }

  startEditing(): void {
    if (!this.params.editable) {
      return;
    }
    this.editing.set(true);
    setTimeout(() => {
      this.headerInput()?.nativeElement.focus();
      this.headerInput()?.nativeElement.select();
    });
  }

  commitEdit(): void {
    this.editing.set(false);
    const newName = this.headerName().trim();
    if (newName && newName !== this.params.displayName) {
      this.params.onHeaderRenamed(this.params.column.getColId(), newName);
    } else {
      this.headerName.set(this.params.displayName);
    }
  }

  onHostClick(): void {
    if (this.editing() || this.contextMenuVisible()) {
      return;
    }
    if (this.clickTimer) {
      clearTimeout(this.clickTimer);
      this.clickTimer = null;
      return;
    }
    this.clickTimer = setTimeout(() => {
      this.clickTimer = null;
      this.startEditing();
    }, 200);
  }

  onHostDblClick(): void {
    if (this.clickTimer) {
      clearTimeout(this.clickTimer);
      this.clickTimer = null;
    }
    this.selectColumn();
  }

  selectColumn(): void {
    this.params.onColumnSelected(this.params.column.getColId());
  }

  onContextMenu(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.contextMenuPos.set({ x: event.clientX, y: event.clientY });
    this.contextMenuVisible.set(true);
  }

  hideContextMenu(): void {
    this.contextMenuVisible.set(false);
  }

  renameFromMenu(): void {
    this.hideContextMenu();
    this.startEditing();
  }

  deleteColumn(): void {
    this.hideContextMenu();
    this.params.onColumnDeleted(this.params.column.getColId());
  }

  sortAscending(): void {
    this.hideContextMenu();
    this.params.setSort('asc');
  }

  sortDescending(): void {
    this.hideContextMenu();
    this.params.setSort('desc');
  }

  toggleTypePicker(event: MouseEvent): void {
    event.stopPropagation();
    if (!this.params.editable) {
      return;
    }
    if (this.typePickerVisible()) {
      this.hideTypePicker();
      return;
    }
    const rect = (event.target as HTMLElement).getBoundingClientRect();
    this.typePickerPos.set({ x: rect.left, y: rect.bottom + 4 });
    this.typePickerVisible.set(true);
  }

  hideTypePicker(): void {
    this.typePickerVisible.set(false);
  }

  selectType(newType: DataInputColumnType): void {
    this.hideTypePicker();
    if (newType === this.colType()) {
      return;
    }
    this.params.onColumnTypeChanged(this.params.column.getColId(), newType);
  }

  isEditable(): boolean {
    return this.params.editable;
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.commitEdit();
    } else if (event.key === 'Escape') {
      this.headerName.set(this.params.displayName);
      this.editing.set(false);
    }
  }
}
